import type { Task } from '@/types/api';
import { BOARD_COLUMNS } from './constants';
import { TaskCard } from '@/features/tasks/TaskCard';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import { TaskCardSkeleton } from '@/components/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import type { BoardView } from './useBoardColumns';
import { ColumnFilterMenu } from './ColumnFilterMenu';
import type { ColumnFilter, TaskStatus } from '@/types/api';

export function DesktopBoard({
  board,
  onOpen,
  showAssignee = false,
  onFilter,
}: {
  board: BoardView;
  onOpen?: ((task: Task) => void) | undefined;
  showAssignee?: boolean;
  onFilter?: ((status: TaskStatus, filter: ColumnFilter) => void) | undefined;
}) {
  const byStatus = new Map(board.columns.map((c) => [c.status, c]));

  /*
    Order comes from the CONSTANT, membership from the DATA.
    Iterating BOARD_COLUMNS alone would render all five whatever the filter said;
    iterating board.columns alone would let the server decide the order. Filtering one
    by the other keeps both properties.
  */
  const columns = BOARD_COLUMNS.filter((column) => byStatus.has(column.status));

  /*
    Tag options come from the cards actually on the board, not the desk vocabulary.
    An option that matches nothing is a dead end you have to try before you learn that.
  */
  const tags = [...new Set(board.columns.flatMap((c) => c.tasks).map((t) => t.tag).filter(Boolean))]
    .sort() as string[];

  return (
    /*
     * Columns are a fixed 300px in a horizontal scroller, not a 5-way grid split.
     * A grid gives Cancelled — usually empty — the same width as Pending, and every
     * card narrows as soon as a sixth status is ever added.
     */
    <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto no-scrollbar p-4">
      {columns.map((column) => {
        const data = byStatus.get(column.status);
        const tasks = data?.tasks ?? [];

        return (
          <section
            key={column.status}
            className="flex min-h-0 w-[300px] shrink-0 flex-col rounded-xl border"
            style={{ borderColor: 'var(--border)', background: 'var(--surface-sunken)' }}
          >
            <header className="flex shrink-0 items-center gap-2 px-3 py-2.5">
              <column.icon size={15} style={{ color: column.colorVar }} />
              <h2 className="text-[13px] font-semibold">{column.label}</h2>
              <span
                className="rounded-md px-1.5 py-px text-[11px] tabular-nums"
                style={{ background: 'var(--surface)', color: 'var(--text-muted)' }}
              >
                {data?.total ?? 0}
              </span>

              {(data?.unread ?? 0) > 0 && (
                <span
                  className="rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
                  style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
                  title={`${data?.unread} with new activity`}
                >
                  {data?.unread}
                </span>
              )}

              {onFilter && data && (
                <span className="ml-auto">
                  <ColumnFilterMenu
                    filter={data.filter}
                    tags={tags}
                    onApply={(next) => onFilter(column.status, next)}
                  />
                </span>
              )}
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto no-scrollbar px-2 pb-2">
              {data?.loading ? (
                // Inside this column's borders. The other four keep their cards.
                <>
                  <TaskCardSkeleton />
                  <TaskCardSkeleton />
                  <TaskCardSkeleton lines={1} />
                </>
              ) : tasks.length === 0 ? (
                <EmptyState
                  size="sm"
                  title={data?.filtered ? 'Nothing matches this filter.' : column.emptyMessage}
                />
              ) : (
                tasks.map((task) => (
                  <TaskCard key={task.id} task={task} onOpen={onOpen} showAssignee={showAssignee} />
                ))
              )}

              {data?.hasMore && (
                <LoadMoreSentinel
                  onVisible={data.loadMore}
                  loading={data.isLoadingMore}
                  remaining={data.total - tasks.length}
                />
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
